import React, { useState, useRef, useEffect } from 'react';
import {
  Typography,
  Box,
  Button,
  Paper,
  Alert,
  Grid,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  FormControlLabel,
  Switch,
  Divider,
  CircularProgress,
  Chip,
  LinearProgress,
  Tabs,
  Tab,
  IconButton,
  Tooltip,
} from '@mui/material';
import { useTheme, alpha } from '@mui/material/styles';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CloseIcon from '@mui/icons-material/Close';
import DescriptionIcon from '@mui/icons-material/Description';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import ArticleIcon from '@mui/icons-material/Article';
import { FaDownload } from 'react-icons/fa';
import { useProcess } from '../context/ProcessContext';
import { useActivityLog } from '../context/ActivityLogContext';
import InterfaceHeader from '../components/InterfaceHeader';
import FavoriteButton from '../components/FavoriteButton';
import API_BASE_URL, { apiCall } from '../config/api';
import { TOOL_COLORS, getCardBackground } from '../constants/colors';

const SEOContentWriter = () => {
  const theme = useTheme();
  const isDarkMode = theme.palette.mode === 'dark';
  const primaryColor = TOOL_COLORS.AI.primary;
  const primaryHover = TOOL_COLORS.AI.hover;
  const primaryLight = TOOL_COLORS.AI.light;
  const { processes, addProcess, updateProcess, removeProcess } = useProcess();
  const {
    addActivityProcess,
    updateActivityProcess,
    setLogs: setActivityLogs,
    completeActivityProcess,
    setIntervalRef,
    clearIntervalRef,
  } = useActivityLog();
  const fileInputRef = useRef(null);
  const intervalRef = useRef(null);
  const processIdRef = useRef(null);
  const downloadTriggeredRef = useRef(false);

  // Mode: 'single' or 'batch'
  const [mode, setMode] = useState('single');

  // Single mode state
  const [topic, setTopic] = useState('');
  const [keywords, setKeywords] = useState('');
  const [wordCount, setWordCount] = useState(750);
  const [tone, setTone] = useState('professional');
  const [includeProductLinks, setIncludeProductLinks] = useState(false);
  const [collectionName, setCollectionName] = useState('');
  const [generating, setGenerating] = useState(false);
  const [article, setArticle] = useState(null);
  const [copied, setCopied] = useState('');

  // Batch mode state
  const [file, setFile] = useState(null);
  const [processing, setProcessing] = useState(false);
  const [processId, setProcessId] = useState(null);
  const [status, setStatus] = useState(null);
  const [isDragging, setIsDragging] = useState(false);

  // Shared state
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  const textFieldStyles = {
    '& .MuiOutlinedInput-root': {
      color: theme.palette.text.primary,
      '& fieldset': { borderColor: isDarkMode ? alpha('#fff', 0.23) : alpha('#000', 0.23) },
      '&:hover fieldset': { borderColor: primaryColor },
      '&.Mui-focused fieldset': { borderColor: primaryColor },
    },
    '& .MuiInputLabel-root.Mui-focused': { color: primaryColor },
  };

  const selectStyles = {
    '& .MuiOutlinedInput-root': {
      '& fieldset': { borderColor: isDarkMode ? alpha('#fff', 0.23) : alpha('#000', 0.23) },
      '&:hover fieldset': { borderColor: primaryColor },
      '&.Mui-focused fieldset': { borderColor: primaryColor },
    },
    '& .MuiInputLabel-root.Mui-focused': { color: primaryColor },
  };

  // Poll for batch status
  useEffect(() => {
    const currentStatus = status?.status || status?.Status;
    const isComplete = currentStatus === 'completed' || currentStatus === 'error';
    if (processId && (processing || (status && !isComplete))) {
      intervalRef.current = setInterval(() => fetchStatus(), 1000);
      setIntervalRef(processId, intervalRef.current);
      return () => { if (intervalRef.current) clearInterval(intervalRef.current); };
    }
  }, [processing, processId, status?.status, status?.Status]);

  useEffect(() => {
    return () => { if (intervalRef.current) clearInterval(intervalRef.current); };
  }, []);

  // --- Single Mode ---
  const handleGenerate = async () => {
    if (!topic.trim()) { setError('Please enter a topic'); return; }
    setGenerating(true);
    setError(null);
    setSuccess(null);
    setArticle(null);

    try {
      const data = await apiCall(`${API_BASE_URL}/api/SEOContentWriter/generate`, {
        method: 'POST',
        body: JSON.stringify({ topic, keywords, wordCount, tone, includeProductLinks, collectionName }),
      });
      if (data.success) {
        setArticle(data.article);
        setSuccess('Article generated successfully!');
      } else {
        setError(data.message || 'Failed to generate article');
      }
    } catch (err) {
      setError(`Error: ${err.message}`);
    } finally {
      setGenerating(false);
    }
  };

  const copyToClipboard = (text, label) => {
    navigator.clipboard.writeText(text);
    setCopied(label);
    setTimeout(() => setCopied(''), 2000);
  };

  // --- Batch Mode ---
  const handleFileSelect = (e) => {
    const selectedFile = e.target.files[0];
    if (selectedFile) validateAndSetFile(selectedFile);
  };

  const validateAndSetFile = (selectedFile) => {
    const ext = selectedFile.name.substring(selectedFile.name.lastIndexOf('.')).toLowerCase();
    if (!['.xlsx', '.xls'].includes(ext)) { setError('Please select a valid Excel (.xlsx, .xls) file'); return; }
    if (selectedFile.size > 50 * 1024 * 1024) { setError('File size must be less than 50MB'); return; }
    setFile(selectedFile);
    setError(null);
  };

  const handleDragEnter = (e) => { e.preventDefault(); e.stopPropagation(); setIsDragging(true); };
  const handleDragLeave = (e) => { e.preventDefault(); e.stopPropagation(); setIsDragging(false); };
  const handleDragOver = (e) => { e.preventDefault(); e.stopPropagation(); };
  const handleDrop = (e) => {
    e.preventDefault(); e.stopPropagation(); setIsDragging(false);
    const droppedFile = e.dataTransfer.files[0];
    if (droppedFile) validateAndSetFile(droppedFile);
  };

  const startBatchProcessing = async () => {
    if (!file) { setError('Please select a file first'); return; }
    downloadTriggeredRef.current = false;
    setStatus(null); setProcessId(null); processIdRef.current = null;
    if (intervalRef.current) { clearInterval(intervalRef.current); intervalRef.current = null; }

    const formData = new FormData();
    formData.append('file', file);
    formData.append('tone', tone);
    formData.append('includeProductLinks', includeProductLinks);

    try {
      setProcessing(true); setError(null); setSuccess(null);
      const data = await apiCall(`${API_BASE_URL}/api/SEOContentWriter/generate-batch`, { method: 'POST', body: formData });
      if (data.success) {
        setProcessId(data.processId);
        processIdRef.current = data.processId;
        addActivityProcess(data.processId, 'SEO Batch Generation', 'SEO Content Writer');
        addProcess({ id: data.processId, name: 'SEO Batch Generation', progress: 0, status: 'processing' });
      } else {
        setError(data.message || 'Failed to start batch processing'); setProcessing(false);
      }
    } catch (err) { setError(`Error: ${err.message}`); setProcessing(false); }
  };

  const fetchStatus = async () => {
    const currentProcessId = processIdRef.current || processId;
    if (!currentProcessId) return;
    try {
      const data = await apiCall(`${API_BASE_URL}/api/SEOContentWriter/status/${currentProcessId}`, { method: 'GET' });
      if (data.success) {
        const s = data.status;
        setStatus(s);
        const pct = s.progressPercentage ?? s.ProgressPercentage ?? 0;
        const total = s.totalItems ?? s.TotalItems ?? 0;
        const processed = s.processedItems ?? s.ProcessedItems ?? 0;
        const logs = s.logs ?? s.Logs ?? [];
        const st = s.status ?? s.Status ?? '';
        updateActivityProcess(currentProcessId, { progressPercentage: pct, totalItems: total, processedItems: processed });
        if (logs.length > 0) setActivityLogs(currentProcessId, logs);
        updateProcess(currentProcessId, { progress: pct, status: st });
        if (st === 'completed' || st === 'error') {
          setProcessing(false);
          if (intervalRef.current) { clearInterval(intervalRef.current); intervalRef.current = null; }
          clearIntervalRef(currentProcessId);
          completeActivityProcess(currentProcessId, st === 'completed');
          if (st === 'completed') {
            setSuccess('Batch SEO generation completed!');
            const awsUrl = s.awsUrl ?? s.AwsUrl ?? null;
            if (!downloadTriggeredRef.current) {
              downloadTriggeredRef.current = true;
              if (awsUrl) {
                const link = document.createElement('a');
                link.href = awsUrl; link.download = 'SEO Articles Result.xlsx';
                document.body.appendChild(link); link.click(); document.body.removeChild(link);
                window.open(awsUrl, '_blank');
              } else {
                downloadResult(currentProcessId);
              }
            }
          } else { setError('Batch processing failed.'); }
        }
      }
    } catch (err) { console.error('Error fetching status:', err); }
  };

  const downloadResult = async (pId) => {
    const downloadProcessId = pId || processId;
    if (!downloadProcessId) return;
    try {
      setError(null);
      const response = await apiCall(`${API_BASE_URL}/api/SEOContentWriter/download/${downloadProcessId}?t=${Date.now()}`, {
        method: 'GET',
        headers: { 'Cache-Control': 'no-cache, no-store, must-revalidate', 'Pragma': 'no-cache', 'Expires': '0' },
      });
      const blob = await response.blob();
      if (blob.size === 0) throw new Error('Downloaded file is empty');
      const now = new Date().toLocaleString('en-US', { timeZone: 'America/New_York', month: 'short', day: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: true });
      const filename = `SEO Articles Result ${now.replace(/,/g, '').replace(/:/g, '-')}.xlsx`;
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.style.display = 'none'; a.href = url; a.download = filename;
      document.body.appendChild(a); a.click();
      setTimeout(() => { if (document.body.contains(a)) document.body.removeChild(a); URL.revokeObjectURL(url); }, 1000);
    } catch (err) { setError(`Download failed: ${err.message}`); }
  };

  const downloadTemplate = async () => {
    try {
      const token = localStorage.getItem('jwtToken');
      const response = await fetch(`${API_BASE_URL}/api/SEOContentWriter/sample`, {
        method: 'GET',
        headers: { ...(token && { 'Authorization': `Bearer ${token}` }) },
      });
      if (!response.ok) throw new Error(`Download failed: ${response.status}`);
      const blob = await response.blob();
      const now = new Date().toLocaleString('en-US', { timeZone: 'America/New_York', month: 'short', day: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: true });
      const filename = `SEO Content Template ${now.replace(/,/g, '').replace(/:/g, '-')}.xlsx`;
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a'); a.href = url; a.download = filename;
      document.body.appendChild(a); a.click(); document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
    } catch (err) { setError(`Template download failed: ${err.message}`); }
  };

  const clearFile = () => { setFile(null); if (fileInputRef.current) fileInputRef.current.value = ''; };

  const handleReset = () => {
    setTopic(''); setKeywords(''); setWordCount(750); setTone('professional');
    setIncludeProductLinks(false); setCollectionName('');
    setGenerating(false); setArticle(null);
    setFile(null); setProcessing(false); setProcessId(null); processIdRef.current = null;
    setStatus(null); setError(null); setSuccess(null);
    if (intervalRef.current) { clearInterval(intervalRef.current); intervalRef.current = null; }
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const statusVal = status?.status ?? status?.Status ?? '';
  const progressPct = status?.progressPercentage ?? status?.ProgressPercentage ?? 0;
  const totalItems = status?.totalItems ?? status?.TotalItems ?? 0;
  const processedItems = status?.processedItems ?? status?.ProcessedItems ?? 0;
  const failedItems = status?.failedItems ?? status?.FailedItems ?? 0;
  const logs = status?.logs ?? status?.Logs ?? [];

  return (
    <Box sx={{ p: { xs: 2, md: 3 }, maxWidth: 1200, mx: 'auto', position: 'relative' }}>
      <FavoriteButton interfaceName="SEO Content Writer" category="ai" />
      <InterfaceHeader
        title="SEO CONTENT WRITER"
        color={primaryColor}
        category="ai"
      />

      <Grid container spacing={3}>
        {/* Left Column */}
        <Grid item xs={12} md={8}>
          {/* Mode Tabs */}
          <Paper sx={{ mb: 3, border: `1px solid ${theme.palette.divider}` }}>
            <Tabs
              value={mode}
              onChange={(e, v) => setMode(v)}
              sx={{
                px: 2, pt: 1,
                '& .MuiTab-root': { textTransform: 'none', fontWeight: 600 },
                '& .Mui-selected': { color: primaryColor },
                '& .MuiTabs-indicator': { backgroundColor: primaryColor },
              }}
            >
              <Tab value="single" label="Single Article" icon={<ArticleIcon />} iconPosition="start" />
              <Tab value="batch" label="Batch Processing" icon={<DescriptionIcon />} iconPosition="start" />
            </Tabs>

            <Box sx={{ p: 3 }}>
              {mode === 'single' ? (
                /* --- Single Article Form --- */
                <>
                  <TextField
                    fullWidth
                    label="Topic / Title Idea"
                    placeholder="e.g., How to Choose the Perfect Area Rug for Your Living Room"
                    value={topic}
                    onChange={(e) => setTopic(e.target.value)}
                    sx={{ mb: 2, ...textFieldStyles }}
                  />

                  <TextField
                    fullWidth
                    label="Target Keywords"
                    placeholder="e.g., area rug, living room decor, rug size guide"
                    value={keywords}
                    onChange={(e) => setKeywords(e.target.value)}
                    helperText="Comma-separated keywords for SEO optimization"
                    sx={{ mb: 2, ...textFieldStyles }}
                  />

                  <Grid container spacing={2} sx={{ mb: 2 }}>
                    <Grid item xs={6}>
                      <FormControl fullWidth size="small" sx={selectStyles}>
                        <InputLabel>Word Count</InputLabel>
                        <Select value={wordCount} label="Word Count" onChange={(e) => setWordCount(e.target.value)}>
                          <MenuItem value={500}>~500 words</MenuItem>
                          <MenuItem value={750}>~750 words</MenuItem>
                          <MenuItem value={1000}>~1000 words</MenuItem>
                        </Select>
                      </FormControl>
                    </Grid>
                    <Grid item xs={6}>
                      <FormControl fullWidth size="small" sx={selectStyles}>
                        <InputLabel>Tone</InputLabel>
                        <Select value={tone} label="Tone" onChange={(e) => setTone(e.target.value)}>
                          <MenuItem value="professional">Professional</MenuItem>
                          <MenuItem value="casual">Casual</MenuItem>
                          <MenuItem value="inspirational">Inspirational</MenuItem>
                        </Select>
                      </FormControl>
                    </Grid>
                  </Grid>

                  <TextField
                    fullWidth
                    label="Collection Name (optional)"
                    placeholder="e.g., Bohemian Rugs, Coastal Living"
                    value={collectionName}
                    onChange={(e) => setCollectionName(e.target.value)}
                    helperText="If provided, the article will naturally reference this Safavieh collection"
                    sx={{ mb: 2, ...textFieldStyles }}
                  />

                  <FormControlLabel
                    control={
                      <Switch
                        checked={includeProductLinks}
                        onChange={(e) => setIncludeProductLinks(e.target.checked)}
                        sx={{ '& .Mui-checked': { color: primaryColor }, '& .Mui-checked + .MuiSwitch-track': { backgroundColor: primaryColor } }}
                      />
                    }
                    label="Include product mentions"
                    sx={{ mb: 2, display: 'block' }}
                  />

                  <Divider sx={{ my: 2 }} />

                  <Box sx={{ display: 'flex', gap: 2 }}>
                    <Button
                      variant="contained"
                      startIcon={generating ? <CircularProgress size={18} color="inherit" /> : <PlayArrowIcon />}
                      onClick={handleGenerate}
                      disabled={generating || !topic.trim()}
                      sx={{
                        flex: 1, py: 1.2, backgroundColor: primaryColor, color: 'white', textTransform: 'none', fontWeight: 600,
                        '&:hover': { backgroundColor: primaryHover },
                        '&:disabled': { backgroundColor: isDarkMode ? alpha('#fff', 0.12) : theme.palette.grey[300], color: isDarkMode ? alpha('#fff', 0.3) : theme.palette.grey[500] },
                      }}
                    >
                      {generating ? 'Generating...' : 'GENERATE ARTICLE'}
                    </Button>
                    <Button
                      variant="outlined"
                      onClick={handleReset}
                      sx={{
                        py: 1.2, textTransform: 'none', fontWeight: 600,
                        borderColor: primaryColor, color: primaryColor,
                        '&:hover': { borderColor: primaryHover, bgcolor: primaryLight },
                      }}
                    >
                      RESET
                    </Button>
                  </Box>
                </>
              ) : (
                /* --- Batch Mode --- */
                <>
                  <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
                    Upload Excel with Topics
                  </Typography>
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                    Required column: Topic. Optional: Keywords, WordCount, CollectionName
                  </Typography>

                  <input ref={fileInputRef} type="file" accept=".xlsx,.xls" onChange={handleFileSelect} style={{ display: 'none' }} />

                  <Box
                    onDragEnter={handleDragEnter} onDragLeave={handleDragLeave} onDragOver={handleDragOver} onDrop={handleDrop}
                    onClick={() => fileInputRef.current?.click()}
                    sx={{
                      border: `2px dashed ${isDragging ? primaryColor : file ? '#4CAF50' : theme.palette.divider}`,
                      borderRadius: 2, p: 3, textAlign: 'center', cursor: 'pointer',
                      bgcolor: isDragging
                        ? primaryLight
                        : file
                          ? (isDarkMode ? 'rgba(76, 175, 80, 0.15)' : 'rgba(76, 175, 80, 0.06)')
                          : 'transparent',
                      transition: 'all 0.3s ease', mb: 2,
                      '&:hover': {
                        borderColor: file ? '#4CAF50' : primaryColor,
                        bgcolor: file
                          ? (isDarkMode ? 'rgba(76, 175, 80, 0.2)' : 'rgba(76, 175, 80, 0.1)')
                          : primaryLight,
                      },
                    }}
                  >
                    {!file ? (
                      <>
                        <CloudUploadIcon sx={{ fontSize: 48, color: isDragging ? primaryColor : 'text.secondary', mb: 1 }} />
                        <Typography variant="body1" gutterBottom>
                          {isDragging ? 'Drop file here' : 'Drop your Excel file here or click to browse'}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          Excel (.xlsx, .xls) files supported
                        </Typography>
                      </>
                    ) : (
                      <>
                        <CheckCircleIcon sx={{ fontSize: 48, color: '#4CAF50', mb: 1 }} />
                        <Typography variant="body1" gutterBottom>1 file selected</Typography>
                        <Typography variant="caption" color="text.secondary">Click or drop to replace</Typography>
                      </>
                    )}
                  </Box>

                  {file && (
                    <Box sx={{
                      display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                      p: 1, bgcolor: 'background.paper', border: `1px solid ${theme.palette.divider}`,
                      borderRadius: 1, transition: 'all 0.2s ease', mb: 1,
                      '&:hover': { bgcolor: isDarkMode ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.04)', borderColor: primaryColor },
                    }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flex: 1, minWidth: 0 }}>
                        <DescriptionIcon sx={{ color: primaryColor, fontSize: 20 }} />
                        <Typography variant="body2" sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {file.name}
                        </Typography>
                        <Typography variant="caption" sx={{ color: 'text.secondary', flexShrink: 0 }}>
                          ({(file.size / 1024).toFixed(1)} KB)
                        </Typography>
                      </Box>
                      <Button size="small" onClick={(e) => { e.stopPropagation(); clearFile(); }}
                        sx={{ color: theme.palette.error.main, minWidth: 'auto', p: 0.5 }}>
                        <CloseIcon fontSize="small" />
                      </Button>
                    </Box>
                  )}

                  {/* Batch options */}
                  <Grid container spacing={2} sx={{ my: 2 }}>
                    <Grid item xs={6}>
                      <FormControl fullWidth size="small" sx={selectStyles}>
                        <InputLabel>Tone</InputLabel>
                        <Select value={tone} label="Tone" onChange={(e) => setTone(e.target.value)}>
                          <MenuItem value="professional">Professional</MenuItem>
                          <MenuItem value="casual">Casual</MenuItem>
                          <MenuItem value="inspirational">Inspirational</MenuItem>
                        </Select>
                      </FormControl>
                    </Grid>
                    <Grid item xs={6}>
                      <FormControlLabel
                        control={
                          <Switch
                            checked={includeProductLinks}
                            onChange={(e) => setIncludeProductLinks(e.target.checked)}
                            sx={{ '& .Mui-checked': { color: primaryColor }, '& .Mui-checked + .MuiSwitch-track': { backgroundColor: primaryColor } }}
                          />
                        }
                        label="Product mentions"
                      />
                    </Grid>
                  </Grid>

                  <Divider sx={{ my: 2 }} />

                  <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                    <Button
                      variant="contained"
                      startIcon={processing ? <CircularProgress size={18} color="inherit" /> : <PlayArrowIcon />}
                      onClick={startBatchProcessing}
                      disabled={processing || !file}
                      sx={{
                        flex: 1, py: 1.2, backgroundColor: primaryColor, color: 'white', textTransform: 'none', fontWeight: 600,
                        '&:hover': { backgroundColor: primaryHover },
                        '&:disabled': { backgroundColor: isDarkMode ? alpha('#fff', 0.12) : theme.palette.grey[300], color: isDarkMode ? alpha('#fff', 0.3) : theme.palette.grey[500] },
                      }}
                    >
                      {processing ? 'Processing...' : 'PROCESS BATCH'}
                    </Button>

                    <Button
                      variant="outlined"
                      startIcon={<FaDownload />}
                      onClick={downloadTemplate}
                      sx={{
                        py: 1.2, textTransform: 'none', fontWeight: 600,
                        borderColor: primaryColor, color: primaryColor,
                        '&:hover': { borderColor: primaryHover, bgcolor: primaryLight },
                      }}
                    >
                      DOWNLOAD TEMPLATE
                    </Button>
                  </Box>
                </>
              )}
            </Box>
          </Paper>

          {/* Article Preview (single mode) */}
          {mode === 'single' && article && (
            <Paper sx={{ p: 3, mb: 3, border: `1px solid ${theme.palette.divider}` }}>
              <Typography variant="h6" sx={{ fontWeight: 700, mb: 2, color: primaryColor }}>
                Generated Article
              </Typography>

              {/* Meta Title */}
              <Box sx={{ mb: 2, p: 2, bgcolor: isDarkMode ? '#1a1a1a' : '#f5f5f5', borderRadius: 1 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.5 }}>
                  <Typography variant="caption" sx={{ fontWeight: 700, color: primaryColor }}>META TITLE</Typography>
                  <Tooltip title={copied === 'metaTitle' ? 'Copied!' : 'Copy'}>
                    <IconButton size="small" onClick={() => copyToClipboard(article.metaTitle, 'metaTitle')}>
                      <ContentCopyIcon fontSize="small" sx={{ color: copied === 'metaTitle' ? '#4CAF50' : 'text.secondary' }} />
                    </IconButton>
                  </Tooltip>
                </Box>
                <Typography variant="body2">{article.metaTitle}</Typography>
                <Typography variant="caption" color="text.secondary">
                  {article.metaTitle?.length || 0}/60 characters
                </Typography>
              </Box>

              {/* Meta Description */}
              <Box sx={{ mb: 2, p: 2, bgcolor: isDarkMode ? '#1a1a1a' : '#f5f5f5', borderRadius: 1 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.5 }}>
                  <Typography variant="caption" sx={{ fontWeight: 700, color: primaryColor }}>META DESCRIPTION</Typography>
                  <Tooltip title={copied === 'metaDesc' ? 'Copied!' : 'Copy'}>
                    <IconButton size="small" onClick={() => copyToClipboard(article.metaDescription, 'metaDesc')}>
                      <ContentCopyIcon fontSize="small" sx={{ color: copied === 'metaDesc' ? '#4CAF50' : 'text.secondary' }} />
                    </IconButton>
                  </Tooltip>
                </Box>
                <Typography variant="body2">{article.metaDescription}</Typography>
                <Typography variant="caption" color="text.secondary">
                  {article.metaDescription?.length || 0}/160 characters
                </Typography>
              </Box>

              {/* Article Title */}
              <Box sx={{ mb: 2, p: 2, bgcolor: isDarkMode ? '#1a1a1a' : '#f5f5f5', borderRadius: 1 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.5 }}>
                  <Typography variant="caption" sx={{ fontWeight: 700, color: primaryColor }}>ARTICLE TITLE</Typography>
                  <Tooltip title={copied === 'title' ? 'Copied!' : 'Copy'}>
                    <IconButton size="small" onClick={() => copyToClipboard(article.title, 'title')}>
                      <ContentCopyIcon fontSize="small" sx={{ color: copied === 'title' ? '#4CAF50' : 'text.secondary' }} />
                    </IconButton>
                  </Tooltip>
                </Box>
                <Typography variant="h6" sx={{ fontWeight: 700 }}>{article.title}</Typography>
              </Box>

              {/* Article Content */}
              <Box sx={{ mb: 2 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                  <Typography variant="caption" sx={{ fontWeight: 700, color: primaryColor }}>
                    CONTENT ({article.wordCount} words)
                  </Typography>
                  <Tooltip title={copied === 'content' ? 'Copied!' : 'Copy HTML'}>
                    <IconButton size="small" onClick={() => copyToClipboard(article.content, 'content')}>
                      <ContentCopyIcon fontSize="small" sx={{ color: copied === 'content' ? '#4CAF50' : 'text.secondary' }} />
                    </IconButton>
                  </Tooltip>
                </Box>
                <Box
                  sx={{
                    p: 3,
                    border: `1px solid ${theme.palette.divider}`,
                    borderRadius: 1,
                    bgcolor: 'background.paper',
                    '& h2': { color: primaryColor, fontSize: '1.3rem', fontWeight: 700, mt: 3, mb: 1 },
                    '& h3': { fontSize: '1.1rem', fontWeight: 600, mt: 2, mb: 1 },
                    '& p': { lineHeight: 1.7, mb: 1.5 },
                  }}
                  dangerouslySetInnerHTML={{ __html: article.content }}
                />
              </Box>
            </Paper>
          )}

          {/* Batch Processing Status */}
          {mode === 'batch' && status && (
            <Paper sx={{ p: 3, border: `1px solid ${theme.palette.divider}` }}>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                <Typography variant="h6" sx={{ fontWeight: 700, color: primaryColor }}>Processing Status</Typography>
                <Chip
                  label={statusVal === 'completed' ? 'Completed' : statusVal === 'error' ? 'Error' : 'Processing...'}
                  size="small"
                  sx={{
                    fontWeight: 600,
                    backgroundColor: statusVal === 'completed' ? alpha('#4caf50', 0.15) : statusVal === 'error' ? alpha('#f44336', 0.15) : alpha(primaryColor, 0.15),
                    color: statusVal === 'completed' ? '#4caf50' : statusVal === 'error' ? '#f44336' : primaryColor,
                  }}
                />
              </Box>

              <LinearProgress
                variant="determinate"
                value={progressPct}
                sx={{ mb: 2, height: 8, borderRadius: 4, backgroundColor: alpha(primaryColor, 0.15), '& .MuiLinearProgress-bar': { backgroundColor: primaryColor, borderRadius: 4 } }}
              />

              <Box sx={{ display: 'flex', gap: 3, mb: 2 }}>
                <Typography variant="body2" color="text.secondary">
                  Progress: <strong>{processedItems}/{totalItems}</strong> articles ({Math.round(progressPct)}%)
                </Typography>
                {failedItems > 0 && (
                  <Typography variant="body2" sx={{ color: '#f44336' }}>Failed: <strong>{failedItems}</strong></Typography>
                )}
              </Box>

              {logs.length > 0 && (
                <Box sx={{
                  maxHeight: 200, overflow: 'auto', p: 1.5,
                  bgcolor: isDarkMode ? '#1a1a1a' : '#f5f5f5', borderRadius: 1,
                  fontFamily: 'monospace', fontSize: '0.75rem', lineHeight: 1.6,
                }}>
                  {logs.map((log, i) => (
                    <Box key={i} sx={{ color: log.includes('Failed') || log.includes('Error') ? '#f44336' : 'text.secondary' }}>
                      {log}
                    </Box>
                  ))}
                </Box>
              )}
            </Paper>
          )}
        </Grid>

        {/* Right Column */}
        <Grid item xs={12} md={4}>
          {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}
          {success && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>{success}</Alert>}

          <Paper sx={{ p: 3, mb: 2, border: `1px solid ${theme.palette.divider}` }}>
            <Typography variant="h6" sx={{ fontWeight: 700, mb: 2, color: primaryColor }}>Instructions</Typography>
            <Typography variant="body2" sx={{ mb: 1, fontWeight: 600 }}>Single Article Mode:</Typography>
            <Typography variant="body2" sx={{ mb: 0.5, pl: 1 }}>1. Enter your blog topic or title idea</Typography>
            <Typography variant="body2" sx={{ mb: 0.5, pl: 1 }}>2. Add target keywords (comma-separated)</Typography>
            <Typography variant="body2" sx={{ mb: 0.5, pl: 1 }}>3. Choose word count and tone</Typography>
            <Typography variant="body2" sx={{ mb: 1.5, pl: 1 }}>4. Click Generate and copy results</Typography>

            <Typography variant="body2" sx={{ mb: 1, fontWeight: 600 }}>Batch Mode:</Typography>
            <Typography variant="body2" sx={{ mb: 0.5, pl: 1 }}>1. Download the template Excel file</Typography>
            <Typography variant="body2" sx={{ mb: 0.5, pl: 1 }}>2. Fill in topics and keywords per row</Typography>
            <Typography variant="body2" sx={{ mb: 0.5, pl: 1 }}>3. Upload and click Process Batch</Typography>
            <Typography variant="body2" sx={{ pl: 1 }}>4. Results auto-download as Excel</Typography>
          </Paper>

          <Paper sx={{ p: 3, mb: 2, border: `1px solid ${theme.palette.divider}` }}>
            <Typography variant="h6" sx={{ fontWeight: 700, mb: 1.5, color: primaryColor }}>SEO Tips</Typography>
            <Typography variant="body2" sx={{ mb: 1 }}>
              <strong>Keywords:</strong> Use your primary keyword in the first 100 words and in at least one H2 subheading.
            </Typography>
            <Typography variant="body2" sx={{ mb: 1 }}>
              <strong>Meta Title:</strong> Keep under 60 characters. Include your primary keyword near the beginning.
            </Typography>
            <Typography variant="body2" sx={{ mb: 1 }}>
              <strong>Meta Description:</strong> Keep under 160 characters. Write a compelling summary that encourages clicks.
            </Typography>
            <Typography variant="body2" sx={{ mb: 1 }}>
              <strong>Content Structure:</strong> Use H2/H3 subheadings every 200-300 words for better readability and SEO.
            </Typography>
            <Typography variant="body2">
              <strong>Word Count:</strong> Longer articles (750-1000+ words) tend to rank better for competitive keywords.
            </Typography>
          </Paper>

          <Paper sx={{ p: 3, mb: 2, border: `1px solid ${theme.palette.divider}` }}>
            <Typography variant="h6" sx={{ fontWeight: 700, mb: 1.5, color: primaryColor }}>How It Works</Typography>
            <Typography variant="body2" sx={{ mb: 1 }}>
              Powered by <strong>Google Gemini AI</strong> with prompts optimized for Safavieh's brand voice: warm, authoritative, and design-forward.
            </Typography>
            <Typography variant="body2" sx={{ mb: 1 }}>
              Each article is structured with SEO best practices including proper heading hierarchy, natural keyword placement, and engaging meta tags.
            </Typography>
            <Typography variant="body2">
              When a collection name is provided, the AI naturally weaves in product references without sounding promotional.
            </Typography>
          </Paper>

          <Paper sx={{ p: 3, border: `1px solid ${theme.palette.divider}` }}>
            <Typography variant="h6" sx={{ fontWeight: 700, mb: 1.5, color: primaryColor }}>Template Format</Typography>
            <Box sx={{ fontFamily: 'monospace', fontSize: '0.75rem', bgcolor: isDarkMode ? '#1a1a1a' : '#f0f0f0', borderRadius: 1, p: 1.5 }}>
              <Box sx={{ display: 'flex', gap: 2, fontWeight: 700, flexWrap: 'wrap' }}>
                <span>Topic</span><span>Keywords</span><span>WordCount</span><span>CollectionName</span>
              </Box>
              <Divider sx={{ my: 0.5 }} />
              <Box sx={{ display: 'flex', gap: 2, color: 'text.secondary', flexWrap: 'wrap' }}>
                <span>How to Choose...</span><span>area rug, decor</span><span>750</span><span>Bohemian</span>
              </Box>
            </Box>
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );
};

export default SEOContentWriter;
